import { FC, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import type { OfferData } from '@appcharge/shared-ui';
import { OfferProgressBar } from '@appcharge/shared-ui';
import useOfferDesign from 'api/useOfferDesign';
import { useProduct } from 'api/useProduct';
import type { ProductsSequence } from 'common/contracts';
import { OfferType } from 'constants/enums';
import {
  getMilestonePreviewActiveMissionIndex,
  MILESTONE_PREVIEW_COLLECTED_POINTS
} from 'utils/milestonePreview';
import {
  MOCK_PROGRESS_BAR_PRODUCTS,
  PROGRESS_BAR_PREVIEW_DEFAULTS
} from 'views/OfferDesign/OfferDesign.consts';
import { OfferPreviewShell } from 'views/Offers/components/OfferPreviewShell';
import { isMilestoneProgressBarDesign } from 'views/Offers/ProgressBars/ProgressBars.utils';

import type { Product } from 'components/ProductsForm/ProductsForm.types';

interface ProgressBarPreviewProps {
  currentOfferDesign: string;
  productsSequence: ProductsSequence[];
}

interface IPreviewSequenceProduct {
  images: { product: string };
  quantity: number;
  quantityDisplay: string;
}

interface IPreviewSequenceItem {
  index: number;
  barSize: number;
  products: IPreviewSequenceProduct[];
}

const PREVIEW_ACTIVE_MISSION_INDEX = 1;
const MOCK_PRODUCT = MOCK_PROGRESS_BAR_PRODUCTS.products[0];

const resolvePreviewProduct = (
  sequenceItem: ProductsSequence,
  products?: Product[]
): IPreviewSequenceProduct => {
  const firstProduct = sequenceItem.products?.[0];
  const productId = firstProduct?.productId;
  const matchedProduct = productId
    ? products?.find((product) => product.productId === productId)
    : undefined;

  let productImage = MOCK_PRODUCT.images.product;
  if (matchedProduct?.images?.[0]?.url) {
    productImage = matchedProduct.images[0].url;
  }

  let quantity = MOCK_PRODUCT.quantity;
  if (firstProduct?.quantity !== undefined) {
    quantity = Number(firstProduct.quantity);
  }

  let quantityDisplay = String(MOCK_PRODUCT.quantityDisplay);
  if (firstProduct?.quantityDisplay) {
    quantityDisplay = firstProduct.quantityDisplay;
  } else if (firstProduct?.quantity !== undefined) {
    quantityDisplay = String(firstProduct.quantity);
  }

  return { images: { product: productImage }, quantity, quantityDisplay };
};

const buildPreviewSequence = (
  productsSequence: ProductsSequence[],
  products?: Product[]
): IPreviewSequenceItem[] => {
  if (!productsSequence?.length) {
    return [
      {
        index: 1,
        barSize: MOCK_PROGRESS_BAR_PRODUCTS.barSize,
        products: [
          {
            images: { product: MOCK_PRODUCT.images.product },
            quantity: MOCK_PRODUCT.quantity,
            quantityDisplay: String(MOCK_PRODUCT.quantityDisplay)
          }
        ]
      }
    ];
  }

  return productsSequence.map((sequenceItem) => {
    let barSize = MOCK_PROGRESS_BAR_PRODUCTS.barSize;
    if (sequenceItem.barSize !== undefined) {
      barSize = sequenceItem.barSize;
    }

    return {
      index: sequenceItem.index,
      barSize,
      products: [resolvePreviewProduct(sequenceItem, products)]
    };
  });
};

export const ProgressBarPreview: FC<ProgressBarPreviewProps> = ({
  currentOfferDesign,
  productsSequence
}) => {
  const { publisherId } = useParams();
  const { getOfferDesign } = useOfferDesign(publisherId, currentOfferDesign);
  const { data: productsData } = useProduct(publisherId).getProducts;

  const offerDesignData = getOfferDesign?.data;

  const previewOfferData = useMemo<OfferData | undefined>(() => {
    if (!offerDesignData) return undefined;

    const products = productsData?.result as Product[] | undefined;
    const progressBar = offerDesignData.progressBar;
    const sequence = buildPreviewSequence(productsSequence, products);

    let activeMissionIndex = PREVIEW_ACTIVE_MISSION_INDEX;
    let collectedPoints: number = PROGRESS_BAR_PREVIEW_DEFAULTS.collectedPoints;
    if (isMilestoneProgressBarDesign(offerDesignData)) {
      activeMissionIndex = getMilestonePreviewActiveMissionIndex(
        sequence.length
      );
      collectedPoints = MILESTONE_PREVIEW_COLLECTED_POINTS;
    }

    return {
      offerId: currentOfferDesign || 'progress-bar-preview',
      offerName: offerDesignData.name || 'Progress Bar Preview',
      offerType: OfferType.PROGRESS_BAR,
      activeMissionIndex,
      collectedPoints,
      schedule: { permanent: true, timeFrames: [] },
      productsSequence: sequence,
      offerUi: {
        ...offerDesignData,
        backgroundColor: progressBar?.backgroundColor
      }
    } as unknown as OfferData;
  }, [offerDesignData, productsSequence, productsData?.result, currentOfferDesign]);

  return (
    <OfferPreviewShell containerClassName="flex justify-center">
      {() =>
        previewOfferData ? (
          <OfferProgressBar data={previewOfferData} onRefetch={() => {}} />
        ) : null
      }
    </OfferPreviewShell>
  );
};
